/* eslint-disable react/prop-types */
import React, { useState, useEffect, useContext } from 'react'
import { useFormState } from 'react-use-form-state'
import { Modal } from 'react-bootstrap'

import i18n from 'live/services/i18n'
import LiveModal from 'live/components/ui/Modal'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import globalNotification from 'live/services/global-notification'

import { apiFetch, apiRequest } from 'plugin-attributes-manager/shared/utils/index'
import { requiredField } from 'plugin-attributes-manager/shared/utils/messages'

import * as S from './styled'
import * as T from '../../types'
import * as Urls from '../../utils/urls'

import { AttributesContext } from '../../Context/AttributesProvider'

interface Props {
    title: string
    showModal: boolean
    setShowModal: (flag: boolean) => void
    attribute?: T.AttributeList
    onClose: () => {}
}

interface SelectType {
    value: string | number
    label: string
}

interface FormState {
    name?: string
    quantity?: SelectType
    description?: string
}

const AttributeModal: React.FC<Props> = ({ title, showModal, setShowModal, attribute, onClose }) => {
    const { attributes, setAttributes, derivedQuantities } = useContext(AttributesContext)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>({})
    const [{ values: formValues, setField }, { label, text, raw }] = useFormState<FormState>({}, { withIds: true })

    useEffect(() => {
        if (attribute) {
            setField('name', attribute?.name)
            setField('quantity', { value: attribute?.derivedQuantity?.id, label: attribute?.derivedQuantity.name })
            setField('description', attribute?.description)
        } else {
            emptyFields()
        }
    }, [attribute])

    const emptyFields = () => {
        setField('name', '')
        setField('quantity', undefined)
        setField('description', '')
    }

    const hasInvalidFields = () => {
        let errorFields = {}
        let hasError = false
        for (const [key, value] of Object.entries(formValues)) {
            if (key === 'description') continue

            if (!value) {
                hasError = true
                errorFields = {
                    ...errorFields,
                    [key]: true
                }
            }
        }

        setError(errorFields)

        return hasError
    }

    const onClickToSubmit = () => {
        if (!hasInvalidFields()) {
            setLoading(true)
            const { name, description, quantity } = formValues
            const payload = {
                name,
                description: description || '',
                derivedQuantityId: quantity.value
            }
            if (attribute) {
                apiRequest(
                    Urls.EDIT_ATTRIBUTE(attribute.id),
                    (data: T.Attribute) => {
                        globalNotification.success(i18n('Attribute Edited Successfully!'))
                        const newList: Map<string | number, T.AttributeList> = attributes
                        const editedAttribute: T.AttributeList = {
                            ...data,
                            derivedQuantity: derivedQuantities.get(data.derivedQuantityId)
                        }
                        newList.set(editedAttribute.id, editedAttribute)
                        setAttributes(newList)
                    },
                    err => {
                        globalNotification.error(err, 8)
                    },
                    () => {
                        emptyFields()
                        setLoading(false)
                        onClose()
                    }
                ).put(payload)
            } else {
                apiRequest(
                    Urls.ATTRIBUTES,
                    (data: T.Attribute) => {
                        globalNotification.success(i18n('Attribute Created Successfully!'))
                        const newList: Map<string | number, T.AttributeList> = attributes
                        const editedAttribute: T.AttributeList = {
                            ...data,
                            derivedQuantity: derivedQuantities.get(data.derivedQuantityId)
                        }
                        newList.set(editedAttribute.id, editedAttribute)
                        setAttributes(newList)
                    },
                    err => {
                        globalNotification.error(err, 8)
                    },
                    () => {
                        emptyFields()
                        setLoading(false)
                        onClose()
                    }
                ).post(payload)
            }
        }
    }

    return (
        <LiveModal show={showModal && formValues}>
            <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <div className="form-group" style={{ flexGrow: 1 }}>
                        <label className="control-label" {...label('name')}>
                            {i18n('Name')} <span className="required">*</span>
                        </label>
                        <S.AssetInputContainer error={error.name}>
                            <input {...text('name')} maxLength={255} />
                            <small>{requiredField}</small>
                        </S.AssetInputContainer>
                    </div>
                    <div className="form-group">
                        <label className="control-label" {...label('quantity')}>
                            {i18n('Quantity')} <span className="required">*</span>
                        </label>
                        <S.SelectContainer error={error.quantity}>
                            <S.Select
                                {...raw('quantity')}
                                onChange={(id: string, item: SelectType) => {
                                    setField('quantity', { value: item.value, label: item.label })
                                }}
                                className="form-group"
                                value={formValues.quantity}
                                options={
                                    derivedQuantities
                                        ? Array.from(derivedQuantities.values()).map((quantity: T.DerivedQuantity) => {
                                              return { label: quantity.name, value: quantity.id }
                                          })
                                        : []
                                }
                                error={error.quantity}
                            />
                            <small>{requiredField}</small>
                        </S.SelectContainer>
                    </div>
                    <div className="form-group" style={{ flexGrow: 1 }}>
                        <label className="control-label" {...label('description')}>
                            {i18n('Description')}
                        </label>
                        <S.AssetInputContainer error={error.description}>
                            <textarea {...text('description')} maxLength={2000} />
                        </S.AssetInputContainer>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <SubmitOrCancel
                    saving={loading}
                    onSubmit={onClickToSubmit}
                    onCancel={() => {
                        emptyFields()
                        onClose()
                    }}
                />
            </Modal.Footer>
        </LiveModal>
    )
}

export default AttributeModal
